import { useState, useEffect, useMemo } from 'react';
import { hasRole } from '@/services/data/roles';
import { getVideosForViralityExpert, getVideosForMonteur, type StaffVideoTask } from '@/services/data/staffVideoContent';
import { getDirectorCalendarShootings, getAllDirectorCalendarShootings, type DirectorCalendarShooting } from '@/services/data/directorCalendar';
import type { CalendarEvent, Channel } from '@/services/business/calendar';

export type StaffRole = 'video_editor' | 'monteur' | 'realisateur' | 'directeur_manager' | null;

// Extended CalendarEvent type for staff tasks
export interface StaffCalendarEvent extends Omit<CalendarEvent, 'type'> {
  type: 'staff_task';
  staffTaskData: StaffVideoTask;
}

// Calendar event type for director shootings
export interface DirectorCalendarEvent extends Omit<CalendarEvent, 'type'> {
  type: 'director_shooting';
  shootingData: DirectorCalendarShooting;
}

interface StaffCalendarData {
  isStaff: boolean;
  staffRole: StaffRole;
  staffTasks: StaffVideoTask[];
  staffEvents: StaffCalendarEvent[];
  directorShootings: DirectorCalendarShooting[];
  directorEvents: DirectorCalendarEvent[];
  isLoading: boolean;
}

/**
 * Convert StaffVideoTask to CalendarEvent-like format
 * for unified rendering in the main calendar
 */
function mapStaffTasksToEvents(tasks: StaffVideoTask[], staffRole: StaffRole): StaffCalendarEvent[] {
  return tasks.map(task => {
    // VE: use derush deadline date; Monteur: use publication date
    let eventDate: Date;

    if (staffRole === 'video_editor' && task.derush_deadline) {
      eventDate = new Date(task.derush_deadline);
    } else if (staffRole === 'monteur' && task.editing_deadline) {
      eventDate = new Date(task.editing_deadline);
    } else if (task.earliest_publication_date) {
      eventDate = new Date(task.earliest_publication_date);
    } else {
      eventDate = new Date('2099-12-31');
    }

    return {
      id: task.id,
      date: eventDate,
      title: task.title || 'Vidéo sans titre',
      type: 'staff_task' as const,
      staffTaskData: task,
      channels: task.channels as Channel[],
      status: 'upcoming' as const,
    };
  });
}

/**
 * Convert DirectorCalendarShooting to CalendarEvent-like format
 */
function mapDirectorShootingsToEvents(shootings: DirectorCalendarShooting[]): DirectorCalendarEvent[] {
  return shootings
    .filter((s): s is DirectorCalendarShooting & { date: string } => Boolean(s.date))
    .map(shooting => ({
      id: `shooting-${shooting.id}`,
      date: new Date(shooting.date),
      title: shooting.title || 'Tournage',
      type: 'director_shooting' as const,
      shootingData: shooting,
      channels: [] as Channel[],
      status: 'upcoming' as const,
    }));
}

interface StaffState {
  isStaff: boolean;
  staffRole: StaffRole;
  staffTasks: StaffVideoTask[];
  directorShootings: DirectorCalendarShooting[];
}

const DEFAULT_STATE: StaffState = {
  isStaff: false,
  staffRole: null,
  staffTasks: [],
  directorShootings: [],
};

/**
 * Determine the user's staff role based on role check results
 */
function determineStaffRole(roles: {
  isVE: boolean;
  isMonteur: boolean;
  isDirector: boolean;
  isDirManager: boolean;
}): StaffRole {
  if (roles.isVE) return 'video_editor';
  if (roles.isMonteur) return 'monteur';
  if (roles.isDirManager) return 'directeur_manager';
  if (roles.isDirector) return 'realisateur';
  return null;
}

/**
 * Load data for a specific staff role
 */
async function loadStaffData(
  role: StaffRole,
  userId: string
): Promise<StaffState> {
  switch (role) {
    case 'video_editor': {
      const result = await getVideosForViralityExpert(userId);
      return {
        isStaff: true,
        staffRole: role,
        staffTasks: result.success ? result.data : [],
        directorShootings: [],
      };
    }
    case 'monteur': {
      const result = await getVideosForMonteur(userId);
      return {
        isStaff: true,
        staffRole: role,
        staffTasks: result.success ? result.data : [],
        directorShootings: [],
      };
    }
    case 'directeur_manager': {
      const result = await getAllDirectorCalendarShootings();
      return {
        isStaff: true,
        staffRole: role,
        staffTasks: [],
        directorShootings: result.success ? result.data : [],
      };
    }
    case 'realisateur': {
      const result = await getDirectorCalendarShootings(userId);
      return {
        isStaff: true,
        staffRole: role,
        staffTasks: [],
        directorShootings: result.success ? result.data : [],
      };
    }
    default:
      return DEFAULT_STATE;
  }
}

/**
 * Hook pour détecter si l'utilisateur est un membre du staff (VE, Monteur, Réalisateur ou Directeur Manager)
 * et charger ses tâches assignées, converties en format CalendarEvent.
 */
export function useStaffCalendarData(userId: string | null): StaffCalendarData {
  const [state, setState] = useState<StaffState>(DEFAULT_STATE);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setState(DEFAULT_STATE);
      setIsLoading(false);
      return;
    }

    const checkRolesAndLoadTasks = async () => {
      setIsLoading(true);

      try {
        const [veResult, monteurResult, directorResult, dirManagerResult] = await Promise.all([
          hasRole(userId, 'virality_expert'),
          hasRole(userId, 'monteur'),
          hasRole(userId, 'realisateur'),
          hasRole(userId, 'directeur_manager'),
        ]);

        const role = determineStaffRole({
          isVE: veResult.success && veResult.data,
          isMonteur: monteurResult.success && monteurResult.data,
          isDirector: directorResult.success && directorResult.data,
          isDirManager: dirManagerResult.success && dirManagerResult.data,
        });

        const newState = await loadStaffData(role, userId);
        setState(newState);
      } catch (error) {
        console.error('Error checking staff roles:', error);
        setState(DEFAULT_STATE);
      }

      setIsLoading(false);
    };

    checkRolesAndLoadTasks();
  }, [userId]);

  const staffEvents = useMemo(
    () => mapStaffTasksToEvents(state.staffTasks, state.staffRole),
    [state.staffTasks, state.staffRole]
  );
  const directorEvents = useMemo(
    () => mapDirectorShootingsToEvents(state.directorShootings),
    [state.directorShootings]
  );

  return {
    isStaff: state.isStaff,
    staffRole: state.staffRole,
    staffTasks: state.staffTasks,
    staffEvents,
    directorShootings: state.directorShootings,
    directorEvents,
    isLoading,
  };
}
